import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  Brush,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MaskValor } from '../../../../../component';
import { IChartProps, StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import { colors } from '../../../../../shared/constants';
import { labelFormatterNumber } from '../../../../../shared/formatters/formatters';
import { ReleasesSalesStockChartData } from './types';

export const ReleasesSalesStockECOMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['releasesSalesStock']}
      Chart={ReleasesSalesStockChart}
      cy={'ReleasesSalesStock'}
      id={'releasesSalesStock'}
      requestStatus={requestStatus['releasesSalesStock']}
    />
  );
};

export const ReleasesSalesStockECO: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['releasesSalesStockECOMAP']}
      Chart={ReleasesSalesStockChart}
      cy={'ReleasesSalesStockECO'}
      id={'releasesSalesStockECO'}
      requestStatus={requestStatus['releasesSalesStockECOMAP']}
    />
  );
};

export const ReleasesSalesStockMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['releasesSalesStockECOMAP']}
      Chart={ReleasesSalesStockChart}
      cy={'ReleasesSalesStockMAP'}
      id={'releasesSalesStockMAP'}
      requestStatus={requestStatus['releasesSalesStockECOMAP']}
    />
  );
};

const ReleasesSalesStockChart: React.FC<
  IChartProps<ReleasesSalesStockChartData[]>
> = ({ data, cy, id, timeframe, divisionValue = 1 }) => {
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [
    labelFormatterNumber,
    labelFormatterNumber,
    labelFormatterNumber,
    labelFormatterNumber,
  ];
  const datakeyType = (e: any) => {
    switch (e) {
      case 'Ano':
        return 'year';
      case 'Trimestre':
        return 'quarter';
      default:
        return 'month';
    }
  };

  // TODO: Refatorar essa lógica
  const idBar = (e: string, t: string) => {
    switch (e.toLowerCase()) {
      case 'releasessalesstockecochart':
        return `eco_${t}`;
      case 'releasessalesstockmapchart':
        return `map_${t}`;
      case 'releasessalesstockecochartmodal':
        return `eco_${t}`;
      case 'releasessalesstockmapchartmodal':
        return `map_${t}`;
      default:
        return t;
    }
  };
  const data100 = data?.map((e: any) => {
    return {
      ...e,
      [idBar(id, 'units_released_count')]:
        e[idBar(id, 'units_released_count')] / divisionValue,
      [idBar(id, 'units_sold_count')]:
        e[idBar(id, 'units_sold_count')] / divisionValue,
      [idBar(id, 'remaining_stock')]:
        e[idBar(id, 'remaining_stock')] / divisionValue,
      [idBar(id, 'units_stocked_on_year')]:
        e[idBar(id, 'units_stocked_on_year')] / divisionValue,
    };
  });

  const renderQuarterTick = (tickProps: any) => {
    const { width, height, x, y, payload, visibleTicksCount } = tickProps;
    const { value, offset } = payload;

    const dataInfo = data100.filter((e) => e[datakeyType(timeframe)] === value);

    let xrect = x + 10;
    let xrectw = width / visibleTicksCount - 20;
    let xtext = x + width / visibleTicksCount / 2;

    if (xrectw < 40) {
      xrect = x;
      xrectw = width / visibleTicksCount;
      xtext = x + width / visibleTicksCount / 2;
    }

    return (
      <>
        <rect x={xrect} y={y - 53} width={xrectw} height="40" fill="#f1f1f1" />
        <text
          x={xtext}
          y={y - 35}
          width={width / visibleTicksCount}
          height={height}
          fill="#333"
          offset={offset}
          className="recharts-text recharts-label"
          textAnchor="middle"
        >
          <tspan>{dataInfo[0][idBar(id, 'vso')]}</tspan>
          <tspan x={xtext} y={y - 20} fontSize="12px">
            VSO
          </tspan>
        </text>
      </>
    );
  };
  return (
    <>
      <ResponsiveContainer width="100%" height="100%" data-cy={`Sales${cy}`}>
        <BarChart
          id={id}
          data={data100}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
          onClick={() => setHasClicked(!hasClicked)}
        >
          <Tooltip
            wrapperStyle={{ visibility: 'visible' }}
            content={
              <StickyTooltip
                hasClicked={hasClicked}
                recordTooltip={recordTooltip}
                setRecordTooltip={setRecordTooltip}
                stopChange={stopChange}
                setStopChange={setStopChange}
                formatterFunctions={formatterFunctions}
              />
            }
          />
          <XAxis height={70} dataKey={datakeyType(timeframe)} />
          {divisionValue > 1 ? (
            <YAxis
              label={{
                value: `X ${divisionValue}`,
                angle: -90,
                position: 'insideLeft',
              }}
            />
          ) : null}
          <YAxis />

          <XAxis
            dataKey={datakeyType(timeframe)}
            axisLine={false}
            tickLine={false}
            interval={0}
            tick={renderQuarterTick}
            height={1}
            scale="band"
            xAxisId="quarter"
          />
          <Legend iconType="circle" />
          <Bar
            dataKey={idBar(id, 'remaining_stock')}
            isAnimationActive={false}
            name="Estoque remanescente"
            fill={colors[0]}
          >
            <LabelList
              dataKey={idBar(id, 'remaining_stock')}
              position="top"
              formatter={(v: any) => MaskValor(v)}
            />
          </Bar>
          <Bar
            dataKey={idBar(id, 'units_released_count')}
            isAnimationActive={false}
            name="Un lançadas"
            fill={colors[1]}
          >
            <LabelList
              dataKey={idBar(id, 'units_released_count')}
              position="top"
              formatter={(v: any) => MaskValor(v)}
            />
          </Bar>
          <Bar
            dataKey={idBar(id, 'units_sold_count')}
            isAnimationActive={false}
            name="Un vendidas"
            fill={colors[2]}
          >
            <LabelList
              dataKey={idBar(id, 'units_sold_count')}
              position="top"
              formatter={(v: any) => MaskValor(v)}
            />
          </Bar>
          <Bar
            dataKey={idBar(id, 'units_stocked_on_year')}
            isAnimationActive={false}
            name="Un em estoque"
            fill={colors[3]}
          >
            <LabelList
              dataKey={idBar(id, 'units_stocked_on_year')}
              position="top"
              formatter={(v: any) => MaskValor(v)}
            />
          </Bar>
          <Brush dataKey="period" height={30} stroke="#8884d8" />
          <Legend iconType="circle" />
        </BarChart>
      </ResponsiveContainer>
    </>
  );
};
